import React, { JSX } from 'react';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { Refresh, WarningFilled, Checkmark } from '@one-thd/sui-icons';
import { useDownloadsContext } from '../../../hooks/useContexts';
import { getSvocId } from '../../../utils/user';
import {
  GetDownloadsList,
  GetSelectedDownloadFile,
} from '../../../api/oms/downloads';
import {
  File,
  IOfflineDownloadRequest,
  IOfflineDownloadListResponse,
  IOfflineDownloadListRequest,
} from '../../../models/downloads';

import '../../../styles/downloads.scss';

export interface OfflineDownloadsProps {
  downloadType: string; // 'OFFLINE_PURCHASE_HISTORY'
  page: number;
}

// TODO: Replace Luxon's DateTime.local().toLocaleString(DateTime.DATE_HUGE) with Day.js equivalent
// const formattedDate = dayjs().format('MMMM D, YYYY');

const getFile = (downloadType: string, selectedFile: File) => {
  const payload: IOfflineDownloadRequest = {
    offlineCSVFileRequest: {
      customerAccountId: getSvocId() || '',
      fileName: selectedFile.fileName,
      downloadType,
    },
  };

  GetSelectedDownloadFile(payload, selectedFile.fileName);
};
//
const readyElement = (): JSX.Element => {
  return (
    <div className="ready-element sui-border-success-strong sui-text-success">
      <Checkmark color="success" style={{ marginLeft: 7 }} />
      <p style={{ paddingLeft: 5 }}>Ready</p>
    </div>
  );
};

const processingElement = (): JSX.Element => {
  return (
    <div className="processing-element">
      <p>Processing</p>
    </div>
  );
};

const failedElement = (): JSX.Element => {
  return (
    <div className="failed-element">
      <p>Failed</p>
    </div>
  );
};

const statusElement = (fileStatus: string) => {
  if (fileStatus === 'COMPLETE') {
    return <div className="status-element">{readyElement()}</div>;
  }
  if (fileStatus === 'PROCESSING') {
    return <div className="status-element">{processingElement()}</div>;
  }
  return <div className="status-element">{failedElement()}</div>;
};

const renderCompleteLink = (downloadType: string, file: File) => {
  return (
    <button
      className="download-button"
      type="button"
      onClick={() => {
        getFile(downloadType, file);
      }}
    >
      Download
    </button>
  );
};

const renderProcessingLink = (
  page: number,
  downloadType: string,
  setDownloadsData: (data: IOfflineDownloadListResponse) => void,
  setDownloadsError: (error: AxiosError) => void,
  setDownloadsLoaded: (flag: boolean) => void,
) => {
  return (
    <div className="refresh">
      <Refresh size="small" />
      <button
        className="refresh-button"
        type="button"
        onClick={() => {
          const payload: IOfflineDownloadListRequest = {
            offlineCSVListRequest: {
              customerAccountId: getSvocId() || '',
              pageSize: 20,
              pageNumber: page,
              listType: downloadType,
              filterCriteria: { fileName: '' },
            },
          };
          setDownloadsLoaded(false);
          GetDownloadsList(
            payload,
            setDownloadsData,
            setDownloadsError,
            setDownloadsLoaded,
          );
        }}
      >
        Refresh
      </button>
    </div>
  );
};

const renderOfflineDownloadLink = (
  file: File,
  downloadType: string,
  page: number,
  setDownloadsData: (data: IOfflineDownloadListResponse) => void,
  setDownloadsError: (error: AxiosError) => void,
  setDownloadsLoaded: (flag: boolean) => void,
) => {
  if (file.fileStatus === 'COMPLETE') {
    return renderCompleteLink(downloadType, file);
  }
  if (file.fileStatus === 'PROCESSING') {
    return renderProcessingLink(
      page,
      downloadType,
      setDownloadsData,
      setDownloadsError,
      setDownloadsLoaded,
    );
  }
  return null;
};

const generateTableRow = (
  files: File[],
  page: number,
  downloadType: string,
  setDownloadsData: (data: IOfflineDownloadListResponse) => void,
  setDownloadsError: (error: AxiosError) => void,
  setDownloadsLoaded: (flag: boolean) => void,
) => {
  return files.map((file: File, index) => {
    return (
      <tbody key={`offline download ${index}`}>
        <tr>
          <td>{DateTime.fromISO(file.createdDate).toFormat('MM/dd/yyyy')}</td>
          <td>{file.fileName}</td>
          <td className="sui-flex sui-justify-center">
            {statusElement(file.fileStatus)}
          </td>
          <td>
            {renderOfflineDownloadLink(
              file,
              downloadType,
              page,
              setDownloadsData,
              setDownloadsError,
              setDownloadsLoaded,
            )}
          </td>
        </tr>
      </tbody>
    );
  });
};

const downloadedFilesElement = (
  files: File[],
  page: number,
  downloadType: string,
  setDownloadsData: (data: IOfflineDownloadListResponse) => void,
  setDownloadsError: (error: AxiosError) => void,
  setDownloadsLoaded: (flag: boolean) => void,
) => {
  return (
    <div>
      <table>
        <thead>
          <tr>
            <th className="date-table-header" style={{ textAlign: 'left' }}>
              Date
            </th>
            <th
              className="file-name-table-header"
              style={{ textAlign: 'left' }}
            >
              Filename
            </th>
            <th className="status-table-header">Status</th>
            <th>{}</th>
          </tr>
        </thead>
        {generateTableRow(
          files,
          page,
          downloadType,
          setDownloadsData,
          setDownloadsError,
          setDownloadsLoaded,
        )}
      </table>
    </div>
  );
};

const noDownloadsElement = () => {
  return (
    <div className="no-downloads-element">
      <WarningFilled color="warning" />
      <p>No Downloads Yet</p>
    </div>
  );
};

export const DownloadsTable = (props: OfflineDownloadsProps) => {
  const { page, downloadType } = props;
  const {
    downloadsData,
    setDownloadsData,
    setDownloadsError,
    setDownloadsLoaded,
  } = useDownloadsContext();

  return (
    <div>
      {downloadsData && downloadsData?.fileList.files.length > 0
        ? downloadedFilesElement(
            downloadsData.fileList.files,
            page,
            downloadType,
            setDownloadsData,
            setDownloadsError,
            setDownloadsLoaded,
          )
        : noDownloadsElement()}
    </div>
  );
};
